import React, { useState } from "react";
import { connect } from "react-redux";
import * as fuiRedux from "../../../redux/fui/index";
import { checkIsWBSEValid } from "../../../redux/fui/index";
import { checkWBSECode, checkMonthlyTicketVolume } from "./CommonFunctions";
import * as FormField from "./IndividualFormFields";
import PropTypes from "prop-types";
import GeneralForm from "./GeneralForm";
import { Grid, MenuItem, InputLabel, FormControl, Select } from "@material-ui/core";
import ValidationDialog from './ValidationDialog';
import useFormValidationWithBorders from './useFormValidationWithBorders';

//Choose a configuration type and enter into quick or full configuration components
/**
 * Ticket Analysis form
 * @author Isabel Sprengers
 * @version 2.0
 * @see Used in component: AppStepper
 */
const TicketAnalysis = ({
  configurationType,
  values,
  updateTAForm,
  aSelectedInstance,
  email,
  setIsTAAllValid,
  history,
  isLoading,
  checkIsWBSEValid,
  isWBSEValid,
  formClickSubmit
}) => {

  const config = configurationType === "quick";

  const [isWBSECodeValid, setIsWBSECodeValid] = useState(true);
  // check whether WBSECode is equal to MD & I WBSE
  const [isWBSECodeMDIValid, setIsWBSECodeMDIValid] = useState(true);
  const [isProjectNameValid, setIsProjectNameValid] = useState(true);
  const [isMonthlyTicketVolumeValid, setIsMonthlyTicketVolumeValid] = useState(
    true
  );
  const [showPassword, setShowPassword] = useState(false);
  const [isTeamNameSelected, setIsTeamNameSelected] = useState(true);

  const [showConfigureSplunk, setShowConfigureSplunk] = useState(false);
  const [selectedSplunkOption, setSelectedSplunkOption] = useState("");
  // Enhanced validation with borders and dialog
  const { validationErrors, showValidationDialog, closeDialog, handleValidationResult, rememberedErrors } = useFormValidationWithBorders(values, isWBSEValid);
  // Fix for red borders not showing on previous steps
  React.useEffect(() => {
    if (rememberedErrors.length > 0) {
      const applyBorders = () => {
        rememberedErrors.forEach(error => {
          const fieldname = error.split(' (')[0];

          // Skip red border for TicketFeed (complex dropdown structure)
          if (fieldname === 'TicketFeed') {
            return;
          }

          const fieldElement = document.getElementById(fieldname);
          if (fieldElement && fieldElement.parentElement && fieldElement.parentElement.parentElement) {
            const fieldset = fieldElement.parentElement.parentElement.querySelector('fieldset');
            if (fieldset) {
              fieldset.style.border = "1px solid #E20000";
            }
          }
        });
      };

      // Apply borders immediately
      applyBorders();

      // Watch for DOM changes and re-apply borders
      const observer = new MutationObserver(applyBorders);
      observer.observe(document.body, { childList: true, subtree: true });

      return () => observer.disconnect();
    }
  }, [rememberedErrors, values]);

  const handleChange = (e, fieldName) => {
    if (fieldName === "TeamName") {
      if (e !== undefined || e !== null) {
        updateTAForm(fieldName, e);
        setIsTeamNameSelected(true);
        if (values.TeamName !== "Others") {
          updateTAForm("TeamNameOthers", "");
        }
      } else {
        setIsTeamNameSelected(false);
      }
    } else {
      e.preventDefault();
      let value = e.target.value;
      updateTAForm(fieldName, value);
      if (fieldName === "WBSEcode") {
        const wbsevalue = value.toUpperCase()
        if (wbsevalue.trim() === "CBYDQ001") {
          setIsWBSECodeMDIValid(false)
        }
        // setIsWBSECodeValid(checkWBSECode(value));
        const isWBSEFormatValid = checkWBSECode(value);
        if (isWBSEFormatValid) {
          checkIsWBSEValid(value)
        }
      }
      if (fieldName === "ProjectName") {
        if (value.length > 32 || value.length < 3) {
          setIsProjectNameValid(false)
        } else {
          setIsProjectNameValid(true)
        }
      }
      if (fieldName === "TicketMonthlyVolume") {
        if (value[0] === "0") {
          setIsMonthlyTicketVolumeValid(false);
        } else {
          setIsMonthlyTicketVolumeValid(checkMonthlyTicketVolume(value));
        }
      }
    }
  };

  // get user's EID from email
  const createdBy = email ? email.split("@")[0] : "";

  const handleChangeMobileNo = (event, fieldName) => {
    const re = /^[0-9]{0,}$/;
    const value = event.target.value;
    re.test(value) && updateTAForm(fieldName, value);
  };

  const isAAAMTermsConditions = values.AAAMTermsConditions === "yes";
  updateTAForm("ConfigurationType", configurationType);
  updateTAForm("DomainContextRoot", aSelectedInstance.DomainContextRoot);
  updateTAForm("Environment", aSelectedInstance.Environment);
  updateTAForm("DeploymentRegion", aSelectedInstance.DeploymentRegion);
  updateTAForm("GDPR", aSelectedInstance.GDPR);
  updateTAForm("RequestNotes", aSelectedInstance.RequestNotes);
  updateTAForm("Tier", aSelectedInstance.Tier);
  updateTAForm("SecondaryPOC", aSelectedInstance.SecondaryPOC);
  updateTAForm(
    "EndToEndUId",
    aSelectedInstance.EndToEndUId === ""
      ? aSelectedInstance.DMSId
      : aSelectedInstance.EndToEndUId
  );

  // Fixed validation logic - all conditions must be true for form to be valid
  const isFormValid = isWBSEValid &&
                     isProjectNameValid &&
                     isTeamNameSelected &&
                     isMonthlyTicketVolumeValid;

  setIsTAAllValid(isFormValid);
  const handleShowPassword = () => {
    if (showPassword) {
      setShowPassword(false);
    } else {
      setShowPassword(true);
    }
  };


  //form validation
  const validateInputFields = (step) => {
    // Only validate on final submit (step 2) or when explicitly called with 'submit'
    if (step !== 'step2' && step !== 'submit') {
      return true; // Skip validation for intermediate steps
    }

    let ispass = true;
    const missingFields = [];

    // Collect all required fields for final validation
    let allRequiredFields = [
      'ProjectName',
      'WBSEcode',
      'TeamName',
      'AdminDistributionGroup'
    ];

    // Add conditional fields based on configuration
    if (!config) {
      allRequiredFields.push('UsersDistributionGroup', 'TicketMonthlyVolume', 'TicketFeed', 'TAapps');
    } else {
      // For quick config (config = true), still add these fields as mandatory for internal users
      allRequiredFields.push('TicketMonthlyVolume', 'TicketFeed', 'TAapps');
    }

    // Check each required field
    for (const fieldname of allRequiredFields) {
      let isFieldInvalid = false;

      if (fieldname === "WBSEcode" && (!values[fieldname] || values[fieldname] === "" || !isWBSEValid)) {
        isFieldInvalid = true;
        missingFields.push(`${fieldname} (${!isWBSEValid ? 'Invalid WBSE code' : 'Required'})`);
      } else if (fieldname === "TicketMonthlyVolume" && (!values[fieldname] || values[fieldname] === "" || !isMonthlyTicketVolumeValid)) {
        isFieldInvalid = true;
        missingFields.push(`${fieldname} ${!isMonthlyTicketVolumeValid ? 'Invalid format' : ''}`);
      } else if (!values[fieldname] || values[fieldname] === "") {
        isFieldInvalid = true;
        missingFields.push(fieldname);
      }

      if (isFieldInvalid) {
        ispass = false;
      }
    }
    // Use the new validation handler
    handleValidationResult(ispass, missingFields, formClickSubmit);
    return ispass;
  }

  return (
    <>
      <GeneralForm
        FormId={4}
        validateInputFields={validateInputFields}
        productname={'ticketAnalysis'}
        ClientContractFields={
          <div style={{ flexGrow: 1 }}>
            <Grid
              container
              spacing={2}
            >
              <Grid item xs={4}>
                {FormField.SAPContractIDField(handleChange, aSelectedInstance)}
              </Grid>
              <Grid item xs={4}>
                {FormField.ClientNameField(handleChange, aSelectedInstance)}
              </Grid>
              <Grid item xs={4}>
                {FormField.EndToEndUIdField(aSelectedInstance)}
              </Grid>
              <Grid item xs={4}>
                {FormField.ProjectNameField(handleChange, values, isProjectNameValid)}
              </Grid>
              <Grid item xs={4}>
                {FormField.WBSECodeField(handleChange, values, isWBSECodeValid, isWBSECodeMDIValid, isWBSEValid, isLoading)}
              </Grid>
              <Grid item xs={4}>
                {FormField.TeamNamesField(handleChange, values, isTeamNameSelected)}
              </Grid >
              {values.TeamName === "Others" &&
                <Grid item xs={4}>
                  {FormField.TeamNameOthers(handleChange, values)}
                </Grid >
              }
            </Grid>
          </div>
        }
        EnvironmentFields={
          <div>
            <div style={{ flexGrow: 1 }}>
              <Grid
                container
                spacing={2}
              >
                <Grid item xs={12}>
                  <p>Environment details have been successfully fetched from the selected instance details provided in the previous step.</p>

                </Grid>

              </Grid>
            </div>
          </div>
        }

        ApplicationFields={
          <div style={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                {FormField.AdminDistributionGroupField(handleChange, values)}
              </Grid>
              {!config && (
                <Grid item xs={6}>
                  {FormField.UsersDistributionGroupField(handleChange, values)}
                </Grid>
              )}
              <Grid item xs={6}>
                {FormField.PlatformTypeField(handleChange, aSelectedInstance)}
              </Grid>
              {aSelectedInstance.AaamPlatformType === "myWizard AiOps Dedicated Platform" ? (
                <Grid item xs={6}>
                  {FormField.TicketFeedMethodField(handleChange, values)}
                </Grid>
              ) : (
                <Grid item xs={6}>
                  {FormField.TicketFeedMethodFieldForManagedPlatform(handleChange, values)}
                </Grid>
              )}

              <Grid item xs={6}>
                {FormField.MonthlyTicketVolumeField(
                  handleChange,
                  values,
                  isMonthlyTicketVolumeValid
                )}
              </Grid>

                <Grid item xs={6}>
                  <FormControl variant="outlined" fullWidth margin="dense">
                    <InputLabel>Configure Splunk Application</InputLabel>
                    <Select
                      value={selectedSplunkOption}
                      onChange={(e) => {
                        setSelectedSplunkOption(e.target.value);
                        updateTAForm("TAapps", e.target.value);
                      }}
                    >
                      <MenuItem value="">
                        <em>-- Select Option --</em>
                      </MenuItem>
                      <MenuItem value="TAwithGenCluster">
                        Ticket Analysis with GenCluster
                      </MenuItem>
                      <MenuItem value="TAonly">
                        Ticket Analysis
                      </MenuItem>
                      <MenuItem value="GenCluster">
                        GenCluster
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
            </Grid>
          </div>
        }
        history={history}
      />
      <ValidationDialog
        open={showValidationDialog}
        onClose={closeDialog}
        errors={validationErrors}
      />
    </>
  );
};

TicketAnalysis.propTypes = {
  /**
   * Users email
   */
  email: PropTypes.string,
  /**
   * The configuration type of the form
   */
  configurationType: PropTypes.string,
  /**
   * Function update TA form
   */
  updateTAForm: PropTypes.func,
  /**
   * Object containing current TA form
   */
  values: PropTypes.object,
  /**
   * Contains selected instance from the stepper
   */
  aSelectedInstance: PropTypes.object,
  /**
   * Sets true if all fields are valid for submit
   */
  setIsTAAllValid: PropTypes.func,
};

const MSTP = (state, ownProps) => ({
  configurationType: state.fui.configurationType,
  values: state.fui.TAForm,
  aSelectedInstance: state.fui.aSelectedInstance,
  email: state.auth.userDetails.email,
  isWBSEValid: state.fui.isWBSEValid,
  isLoading: state.fui.isLoading,
});

export default connect(MSTP, { updateTAForm: fuiRedux.updateTAForm, checkIsWBSEValid })(
  TicketAnalysis
);
